import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  forwardRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { Store } from '@ngrx/store';

import { closeBrackets, closeBracketsKeymap } from '@codemirror/autocomplete';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import {
  bracketMatching,
  foldGutter,
  foldKeymap,
  indentOnInput,
  indentUnit,
} from '@codemirror/language';
import { Diagnostic, lintGutter, linter, lintKeymap } from '@codemirror/lint';
import { searchKeymap } from '@codemirror/search';
import { Compartment, EditorState, Extension } from '@codemirror/state';
import {
  EditorView,
  drawSelection,
  highlightActiveLine,
  highlightActiveLineGutter,
  keymap,
  lineNumbers,
} from '@codemirror/view';

import {
  sensikaDarkTheme,
  sensikaLightTheme,
} from '../../../core/components/query-editor/theme/theme';

import { selectCoreDarkMode } from 'src/app/core/reducers';

/**
 * The offset + message of a JSON.parse failure, or null when the text parses.
 */
export interface JsonParseState {
  valid: boolean;
  message: string | null;
  position: number | null;
}

/**
 * Reusable, wizard-agnostic JSON code editor built on CodeMirror 6 (the
 * query-editor precedent), exposed as a ControlValueAccessor so it binds a
 * FormControl<string>. @codemirror/lang-json is not installed, so linting is a
 * light JSON.parse pass surfaced through @codemirror/lint (gutter marker +
 * underline at the error offset); the parse verdict is also emitted via
 * (parseStateChange) for callers that gate on parseability. Line numbers,
 * fold/bracket matching, search and history come from the standard CodeMirror
 * modules; the dark/light chrome reuses the query-editor theme so the two
 * editors look identical, and it swaps live with the app dark-mode signal.
 */
@Component({
  selector: 'app-json-editor',
  templateUrl: './json-editor.component.html',
  styleUrls: ['./json-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => JsonEditorComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class JsonEditorComponent
  implements AfterViewInit, OnDestroy, ControlValueAccessor
{
  @ViewChild('host', { static: true })
  private readonly host!: ElementRef<HTMLDivElement>;

  @Input()
  set readOnly(value: boolean) {
    this._readOnly = value;
    this.applyReadOnly();
  }
  get readOnly(): boolean {
    return this._readOnly;
  }

  @Output() readonly parseStateChange = new EventEmitter<JsonParseState>();
  @Output() readonly editorBlur = new EventEmitter<void>();

  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);

  private view: EditorView | null = null;
  private readonly themeCompartment = new Compartment();
  private readonly readOnlyCompartment = new Compartment();

  private _readOnly = false;
  private isDarkMode = false;
  private value = '';
  // Guards the writeValue -> dispatch -> updateListener loop from re-emitting.
  private programmaticEdit = false;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    this.store
      .select(selectCoreDarkMode)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isDarkMode: boolean) => {
        this.isDarkMode = isDarkMode;
        this.applyTheme();
      });
  }

  /** Build the editor once the host element exists. */
  ngAfterViewInit(): void {
    this.view = new EditorView({
      state: EditorState.create({
        doc: this.value,
        extensions: this.buildExtensions(),
      }),
      parent: this.host.nativeElement,
    });
  }

  /** Tear the editor down to release the CodeMirror DOM + listeners. */
  ngOnDestroy(): void {
    this.view?.destroy();
    this.view = null;
  }

  /** CVA: push a new document into the editor without emitting a change. */
  writeValue(value: string): void {
    this.value = value ?? '';
    if (this.view) {
      this.programmaticEdit = true;
      this.view.dispatch({
        changes: {
          from: 0,
          to: this.view.state.doc.length,
          insert: this.value,
        },
      });
      this.programmaticEdit = false;
    }
    this.emitParseState(this.value);
  }

  /** CVA: register the form's change callback. */
  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  /** CVA: register the form's touched callback. */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /** CVA: reflect a disabled FormControl as a read-only editor. */
  setDisabledState(isDisabled: boolean): void {
    this.readOnly = isDisabled;
  }

  /**
   * Assemble the editor extensions. The theme and read-only state live in
   * compartments so they can be reconfigured without rebuilding the editor.
   *
   * @return the CodeMirror extension list
   */
  private buildExtensions(): Extension[] {
    return [
      lineNumbers(),
      highlightActiveLineGutter(),
      highlightActiveLine(),
      drawSelection(),
      history(),
      foldGutter(),
      indentOnInput(),
      indentUnit.of('  '),
      bracketMatching(),
      closeBrackets(),
      lintGutter(),
      linter((view) => this.lintDocument(view)),
      keymap.of([
        ...closeBracketsKeymap,
        ...defaultKeymap,
        ...searchKeymap,
        ...historyKeymap,
        ...foldKeymap,
        ...lintKeymap,
      ]),
      EditorView.lineWrapping,
      this.themeCompartment.of(this.currentTheme()),
      this.readOnlyCompartment.of(EditorState.readOnly.of(this._readOnly)),
      EditorView.updateListener.of((update) => {
        if (update.docChanged && !this.programmaticEdit) {
          const doc = update.state.doc.toString();
          this.value = doc;
          this.onChange(doc);
          this.emitParseState(doc);
        }
      }),
      EditorView.domEventHandlers({
        blur: () => {
          this.onTouched();
          this.editorBlur.emit();
          return false;
        },
      }),
    ];
  }

  /**
   * Diagnostics source: a single JSON.parse pass mapped to a lint marker at the
   * failing offset. Empty documents are treated as clean (the caller decides
   * whether an empty document is acceptable).
   *
   * @param view the editor view being linted
   * @return the diagnostics for the current document
   */
  private lintDocument(view: EditorView): Diagnostic[] {
    const doc = view.state.doc.toString();
    let diagnostics: Diagnostic[] = [];
    if (doc.trim().length > 0) {
      try {
        JSON.parse(doc);
      } catch (error) {
        const message = (error as Error).message;
        const position = this.errorOffset(message, doc.length);
        diagnostics = [
          {
            from: position,
            to: Math.min(position + 1, doc.length),
            severity: 'error',
            message,
          },
        ];
      }
    }
    return diagnostics;
  }

  /**
   * Emit the parse verdict so callers can gate validate/save on parseability.
   *
   * @param doc the current document text
   */
  private emitParseState(doc: string): void {
    let state: JsonParseState;
    if (doc.trim().length === 0) {
      state = { valid: false, message: null, position: null };
    } else {
      try {
        JSON.parse(doc);
        state = { valid: true, message: null, position: null };
      } catch (error) {
        const message = (error as Error).message;
        state = {
          valid: false,
          message,
          position: this.errorOffset(message, doc.length),
        };
      }
    }
    this.parseStateChange.emit(state);
  }

  /**
   * Pull the character offset out of a V8 JSON.parse message
   * ("... at position N"), clamped to the document length.
   *
   * @param message the parse error message
   * @param docLength the document length
   * @return the best-effort error offset
   */
  private errorOffset(message: string, docLength: number): number {
    const match = /position (\d+)/.exec(message);
    let offset = 0;
    if (match) {
      offset = Math.min(Number(match[1]), Math.max(docLength - 1, 0));
    }
    return offset;
  }

  /** Reconfigure the theme compartment to the current dark-mode state. */
  private applyTheme(): void {
    this.view?.dispatch({
      effects: this.themeCompartment.reconfigure(this.currentTheme()),
    });
  }

  /** Reconfigure the read-only compartment to the current flag. */
  private applyReadOnly(): void {
    this.view?.dispatch({
      effects: this.readOnlyCompartment.reconfigure(
        EditorState.readOnly.of(this._readOnly),
      ),
    });
  }

  /**
   * @return the query-editor theme matching the current dark-mode state
   */
  private currentTheme(): Extension {
    return this.isDarkMode ? sensikaDarkTheme : sensikaLightTheme;
  }
}
